import * as Utils from "../class.TransporterUtils";
import MotorParseable from "./class.MotorParseable";

/**
 * This action is the base for both "DynamicGraph" and "DelayedGraph"
 * (formerly "FillInTheBlankGraph").
 *
 * They are differentiated by the presence of the `pEIndicesToDelay` input.
 *    - "Dynamic" renders everything on the graph immediately.
 *    - "Delayed" causes the preview bubble to change to the student's graph
 * 			on submit.
 */
export default class DynamicGraph extends MotorParseable {
	initialize() {
		super.initialize();

		this.renderIndex = 0; // Increment when rendering response.
		this.hasDelayedOutputGraph = false;
	}

	onRenderProblem($problemDiv) {
		super.onRenderProblem($problemDiv);

		// Don't focus on blanks when problem has both blanks and dropdowns/sliders.
		if (
			this.model.mathjax &&
			!(
				this.model.mathjax.includes("\\dropdown") ||
				this.model.mathjax.includes("\\slider")
			)
		) {
			Utils.focusWhenPossible($problemDiv);
		}
	}

	getNewDriver() {
		return new Motors.Drivers.DynamicGraphDriver();
	}

	getAnswerKey() {
		return this.model.answerKey || "values";
	}

	getPreviewKey() {
		return this.model.previewKey || "newJax";
	}

	getStartSignal() {
		// Parse pseudoElements (array of PseudoGraphElement objects).
		let pseudoElements = (this.model.pseudoElements || []).map((pE) => {
			let ret = {
				type: pE.elType || "polynomial",
				eqnData: this.tryJSONParse(pE.eqnData),
			};
			let options = [
				{name: "minDom", type: "number"},
				{name: "maxDom", type: "number"},
				{name: "isInverted", type: "boolean"},
				{name: "width", type: "number"},
				{name: "color", type: "string"},
			];
			options.forEach((opt) => {
				if (pE.hasOwnProperty(opt.name)) {
					ret[opt.name] =
						opt.type === "number" ? parseFloat(pE[opt.name]) : pE[opt.name];
				}
			});
			return ret;
		});

		let pEIndicesToDelay = this.tryJSONParse(
			this.model.pEIndicesToDelay || "[]"
		);
		if (!Array.isArray(pEIndicesToDelay)) {
			pEIndicesToDelay = [];
		}
		this.hasDelayedOutputGraph = pEIndicesToDelay.length > 0;

		let persistentValues = Array.isArray(this.driverOutputs.values)
			? this.driverOutputs.values
			: undefined;

		// Extract data to initialize coordinate plane.
		let bounds = this.tryJSONParse(this.model.graphBounds || "[]");
		let [minX, maxX, minY, maxY] = [-10, 10, -5, 5];
		if (!Array.isArray(bounds) || bounds.length < 4) {
			console.log(
				"Error in Graph Bounds input, need four entries.  Using defaults [-10, 10, -5, 5]."
			);
		} else {
			[minX, maxX, minY, maxY] = bounds;
		}

		// Parse slideData (array of SlideData objects).
		let slideData = (this.model.slideData || []).map((s) => {
			let range = [-5, 5];
			let [min, max] = range;

			if (s.range) {
				range = this.tryJSONParse(s.range);
			}
			if (!Array.isArray(range) || range.length < 2) {
				console.log(
					"Error in Slider Data input, need min and max for range.  Using defaults -5, 5."
				);
			} else {
				[min, max] = range;
			}

			let value = parseFloat(s.value || "0");
			value = isFinite(value) ? value : 0;

			return {
				min,
				max,
				step: isFinite(s.step) ? s.step : 1,
				scale: s.scale || "lin",
				value,
			};
		});

		// // convert all elements of 2D array to string
		// const dropdownOptions = this.tryJSONParse(this.model.dropdownOptions).map(
		// 	x => x.map(y => String(y))
		// );
		const dropdownOptions = [];

		return {
			// Graph stuff
			pseudoElements,
			pEIndicesToDelay,
			persistentValues,
			graphBackground: {
				minX,
				maxX,
				minY,
				maxY,
				// Use default for now, not sure if curric should be able to change tick size.
				tickStepX: 1,
				tickStepY: 1,
				backgroundImage: this.model.backgroundImage,
			},
			width: this.model.width || 600,

			// Mathjax options
			mathjax: this.model.mathjax,
			values: persistentValues ? persistentValues.map((v) => String(v)) : [],
			maxLength: this.tryJSONParse(this.model.maxLength),
			allNumeric: true, // Graph can only have numeric inputs
			dropdownOptions,
			slideData,
		};
	}

	getDisabledSignal() {
		return {
			disabled: true,
		};
	}

	getCachedResponseSignal(data) {
		return {
			persistentValues: data.values.slice(),
		};
	}

	onRenderResponse($responseDiv) {
		this.renderIndex++;
		this.setSubmittable(this.isResponseSubmittable());

		if (this.previewKey === "newJax") {
			const preview = this.driverOutputs.newJax;
			Utils.renderJax(
				this.mainView.$el,
				$responseDiv,
				preview !== undefined ? String(preview) : ""
			);
		} else if (this.previewKey === "previewJax") {
			if (this.model.previewJax) {
				let previewStr = this.model.previewJax;
				let i = 0;
				while (previewStr.indexOf("\\blank") >= 0) {
					let str = this.driverOutputs.values[i++];
					str = Utils.sanitizeJax(str);
					str = Utils.reformatJax(str);
					previewStr = previewStr.replace(
						"\\blank",
						str ? `{${str}}` : "\\boxed{\\phantom{??}}"
					);
				}
				Utils.renderJax(this.mainView.$el, $responseDiv, previewStr);
			}
		} else if (this.previewKey === "values") {
			$responseDiv.text(
				(this.driverOutputs.values || []).filter((n) => !!n).join(", ")
			);
		}
	}

	isResponseSubmittable() {
		return (
			this.driverOutputs.values &&
			this.driverOutputs.values.every((value) => value !== "")
		);
	}

	/**
	 * How to change the $submitDiv contents on submission.
	 * If this motor has a delayed outputGraph, show it on response.
	 * Otherwise, try submitJax or the default.
	 */
	async onRenderSubmit($submitDiv) {
		if (this.hasDelayedOutputGraph) {
			if (this.driverOutputs.outputGraph.props.elements.length) {
				$submitDiv.html("<div class='preview-graph' />");
				await Motors.render(
					this.driverOutputs.outputGraph,
					$submitDiv.children()[0]
				);
				await $submitDiv.prepend("Here is my graph:");
			} else {
				await $submitDiv.text("I'm done.");
			}
		} else {
			super.onRenderSubmit($submitDiv);
			if (this.model.submitJax) {
				let submitStr = this.model.submitJax;
				let i = 0;
				while (submitStr.indexOf("\\blank") >= 0) {
					let str = this.driverOutputs.values[i++];
					str = Utils.sanitizeJax(str);
					str = Utils.reformatJax(str);
					submitStr = submitStr.replace(
						"\\blank",
						str || "\\boxed{\\phantom{??}}"
					);
				}
				await Utils.renderJax(this.mainView.$el, $submitDiv, submitStr);
			}
		}
	}
}
